<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">{{organizationName}} organizational dashboard</h1>

                    <a :href="`/#/admin/organizations/users/create/${tid}`" type="button" class="btn btn-link btn-sm">
                        <i class="fa fa-user-plus" aria-hidden="true"></i>
                        <span>Add user</span>
                    </a>

                    <a :href="`/#/admin/organizations/workspaces/create/${tid}`" type="button" class="btn btn-link btn-sm">
                        <i class="fa fa-plus" aria-hidden="true"></i>
                        <span>Add workspace</span>
                    </a>
                </div>
                <div class="col-lg-5 col-sm-6 text-right">
                    <a v-if="isSystemAdmin || isGlobalAdmin" :href="`/#/admin/organizations/manage`" role="button" class="btn btn-success text-white" style="padding: 0.7em 2em;">
                        <i class="fa fa-bank" aria-hidden="true"></i>
                        <span>Manage organizations</span>
                    </a>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div class="container">
            <div class="form-group" style="margin-bottom: 40px;">
                <div class="input-group mb-3">
                    <input v-model="searchPhrase" @keyup.enter="handleSearch" class="form-control form-control-lg" type="search" placeholder="Type to search for user, workspace or portfolio in your organization...">
                    <div class="input-group-append">
                        <button @click="handleSearch" class="btn btn-outline-secondary" type="button" style="width: 60px;" :disabled="searchPhrase.length<=1">
                            <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="row" style="margin-bottom: 40px;">
                <div class="col-sm-7">
                    <div class="card bg-light">
                        <div class="card-body">
                            <h5 class="card-title">{{organizationName}}</h5>
                            <h6 class="card-subtitle mb-2 text-muted">System admin: {{systemAdminEmail}}</h6>
                            <h6 class="card-subtitle mb-2 text-muted">Version: {{version}}</h6>
                            <!--<h6 class="card-subtitle mb-2 text-muted">Organization admin: janusz@transparentchoice.com</h6>-->
                            <p class="card-text">{{organizationDescription}}</p>
                            <!--<a :href="`/#/organizations/${id}`" class="card-link" target="_blank">Dashboard</a>-->
                            <a :href="`/#/admin/organizations/manage/${tid}`" class="card-link">Manage</a>
                            <a :href="`/#/admin/organizations/manage/branding/${tid}`" class="card-link">Branding</a>
                            <a :href="`/#/admin/organizations/manage/sso/${tid}`" class="card-link">SSO</a>
                            <!--<a :href="`/#/admin/organizations/features/${tid}`" class="card-link">Features</a>
                             <a :href="`/#/admin/organizations/recommendations/${tid}`" class="card-link">Recommendations</a>-->
                        </div>
                    </div>
                </div>

                <div class="col-sm-5">
                    <table class="table table-sm table-striped">
                        <thead>
                            <tr>
                                <th colspan="2">Usage stats</th>
                                <!--<th class="text-right"><a href="/">Send report</a></th>-->
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th scope="row">Users</th>
                                <td class="fg-color-blue" title="All users and global admins">{{numberOfUsers}}</td>
                            </tr>
                            <tr>
                                <th scope="row">Workspaces</th>
                                <td class="fg-color-blue">{{numberOfWorkspaces}}</td>
                            </tr>
                            <tr>
                                <th scope="row">Portfolios</th>
                                <td class="fg-color-blue">{{numberOfPortfolios}}</td>
                            </tr>
                            <tr>
                                <th scope="row">Projects</th>
                                <td class="fg-color-blue">{{numberOfProjects}}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="row" style="margin-bottom: 40px;">
                <div class="col-md-2 text-center" :class="numberOfAccountRequests > 0 ? 'fg-color-orange' : 'fg-color-blue'">
                    <h1>{{numberOfAccountRequests}}</h1>
                    <a :href="`/#/admin/organizations/users/requests/${tid}`"
                       :class="numberOfAccountRequests > 0 ? 'fg-color-orange' : 'fg-color-blue'">
                        Account requests
                    </a>
                </div>

                <div class="col-md-2 text-center fg-color-blue" title="Only users who are not locked">
                    <h1>{{numberOfActiveUsers}}</h1>
                    <span>Active Users</span>
                </div>

                <div class="col-md-2 text-center fg-color-blue">
                    <h1>{{numberOfGlobalAdmins}}</h1>
                    <span>Global admins</span>
                </div>

                <div class="col-md-2 text-center" :class="numberOfLockedUsers > 0 ? 'fg-color-red' : 'fg-color-blue'">
                    <h1>{{numberOfLockedUsers}}</h1>
                    <span>Locked users</span>
                </div>

                <div class="col-md-2 text-center fg-color-blue">
                    <h1>{{numberOfActiveSessions}}</h1>
                    <span>Sessions</span>
                </div>

                <!--<div class="col-md-2 text-center fg-color-blue">
                    <h1>{{numberOfAccessLinks}}</h1>
                    <span>Access links</span>
                </div>-->
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import Component, { mixins } from 'vue-class-component';
    import PageBase from "@common/PageBase";
    import VuePageQueries from "@Organizations/OrganizationUnits/Domain/queries-vue-component";
    import VuePageCommands from "@Organizations/OrganizationUnits/Domain/commands-vue-component";
    import VuePageUserQueries from "@Organizations/Users/Domain/queries-vue-component";

    @Component
    export default class DashboardPage extends mixins(PageBase, VuePageQueries, VuePageCommands, VuePageUserQueries) {
        searchPhrase = '';

        mounted() {
            this.setTitle("Admin dashboard");
            this.runCurrentUserQueryV1(false, false, true);
            this.runOrganizationAdminDetailsQueryV1(this.tid);
        }

        handleSearch() {
            if (this.searchPhrase.length >= 2)
                this.$router.push(`/admin/organizations/search/${this.tid}/${this.searchPhrase}`);
        }

        get organizationAdminDetails() { return this.model.OrganizationAdminDetails; }
        get userSessionDetails() { return this.model.UserSessionDetails; } 

        get organizationName() { return this.organizationAdminDetails?.OrganizationName; }
        get organizationDescription() { return this.organizationAdminDetails?.OrganizationDescription; }
        get systemAdminEmail() { return this.organizationAdminDetails?.SystemAdminEmail; }
        get numberOfWorkspaces() { return this.organizationAdminDetails?.NumberOfWorkspaces; }
        get numberOfPortfolios() { return this.organizationAdminDetails?.NumberOfPortfolios; }
        get numberOfProjects() { return this.organizationAdminDetails?.NumberOfProjects; }
        get numberOfAccountRequests() { return this.organizationAdminDetails?.NumberOfAccountRequests; }
        get numberOfUsers() { return this.organizationAdminDetails?.NumberOfUsers; }
        get numberOfActiveUsers() { return this.numberOfUsers - this.numberOfLockedUsers; }
        get numberOfGlobalAdmins() { return this.organizationAdminDetails?.NumberOfGlobalAdmins; }
        get numberOfLockedUsers() { return this.organizationAdminDetails?.NumberOfLockedUsers; }
        get numberOfActiveSessions() { return this.organizationAdminDetails?.NumberOfActiveSessions; }
        get numberOfAccessLinks() { return this.organizationAdminDetails?.NumberOfAccessLinks; }
        get version() { return this.organizationAdminDetails?.Version; }

        get isSystemAdmin() { return this.userSessionDetails?.IsSystemAdmin || false; }
        get isGlobalAdmin() { return this.userSessionDetails?.IsGlobalAdmin || false; }
    }
</script>

<style scoped>
</style>